import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Rnd } from "react-rnd";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { Upload, Type, Image as ImgIcon, Save, Trash2, Plus, Copy } from "lucide-react";

const uid = () => Math.random().toString(36).slice(2, 10);

export default function Editor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef();
  const canvasRef = useRef();

  const [tpl, setTpl] = useState({
    name: "Untitled Template",
    image_path: "",
    image_width: 0,
    image_height: 0,
    fields: [],
  });
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [canvasScale, setCanvasScale] = useState(1);

  useEffect(() => {
    if (id) {
      (async () => {
        try {
          const { data } = await api.get(`/templates/${id}`);
          setTpl(data);
        } catch {
          toast.error("Template not found");
          navigate("/templates");
        }
      })();
    }
  }, [id, navigate]);

  // fit canvas to container
  useEffect(() => {
    const compute = () => {
      if (!canvasRef.current || !tpl.image_width) return;
      const cw = canvasRef.current.parentElement.clientWidth - 32;
      const ch = window.innerHeight - 260;
      const sw = cw / tpl.image_width;
      const sh = ch / tpl.image_height;
      setCanvasScale(Math.min(sw, sh, 1));
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [tpl.image_width, tpl.image_height]);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads/image", fd);
      setTpl(t => ({ ...t, image_path: data.path, image_width: data.width, image_height: data.height }));
      toast.success("Template uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const addField = (type) => {
    const cx = (tpl.image_width || 400) / 2;
    const cy = (tpl.image_height || 250) / 2;
    const f = {
      id: uid(),
      key: type === "photo" ? "photo" : `field_${tpl.fields.length + 1}`,
      label: type === "photo" ? "Photo" : `Field ${tpl.fields.length + 1}`,
      type,
      x: cx - 60, y: cy - 20,
      width: type === "photo" ? 120 : 200,
      height: type === "photo" ? 150 : 40,
      font_size: 20,
      color: "#000000",
      font_weight: "normal",
      align: "left",
      sample_value: type === "photo" ? "" : "Sample",
    };
    setTpl(t => ({ ...t, fields: [...t.fields, f] }));
    setSelected(f.id);
  };

  const updateField = (fid, patch) => {
    setTpl(t => ({ ...t, fields: t.fields.map(f => f.id === fid ? { ...f, ...patch } : f) }));
  };

  const removeField = (fid) => {
    setTpl(t => ({ ...t, fields: t.fields.filter(f => f.id !== fid) }));
    if (selected === fid) setSelected(null);
  };

  const duplicateField = (fid) => {
    const f = tpl.fields.find(x => x.id === fid);
    if (!f) return;
    const nf = { ...f, id: uid(), key: f.key + "_copy", x: f.x + 20, y: f.y + 20 };
    setTpl(t => ({ ...t, fields: [...t.fields, nf] }));
  };

  const save = async () => {
    if (!tpl.image_path) { toast.error("Upload a template image first"); return; }
    if (!tpl.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const body = { name: tpl.name, image_path: tpl.image_path, image_width: tpl.image_width, image_height: tpl.image_height, fields: tpl.fields };
      if (id) {
        await api.put(`/templates/${id}`, body);
        toast.success("Template updated");
      } else {
        const { data } = await api.post("/templates", body);
        toast.success("Template created");
        navigate(`/templates/${data.id}/edit`, { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sel = tpl.fields.find(f => f.id === selected);

  return (
    <div className="space-y-4" data-testid="editor-page">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <input
          value={tpl.name}
          onChange={e => setTpl(t => ({ ...t, name: e.target.value }))}
          className="brutal-input flex-1 min-w-[240px] font-display font-bold text-lg"
          placeholder="Template name"
          data-testid="tpl-name"
        />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-xs px-4 py-3 brutal-border brutal-shadow-sm brutal-hover disabled:opacity-60" data-testid="upload-tpl-btn">
          <Upload className="w-4 h-4"/> {uploading ? "..." : tpl.image_path ? "Replace" : "Upload Image"}
        </button>
        <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" data-testid="tpl-file-input"/>
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-[#002FA7] text-white font-bold uppercase tracking-widest text-xs px-4 py-3 brutal-border brutal-shadow brutal-hover disabled:opacity-60" data-testid="save-tpl-btn">
          <Save className="w-4 h-4"/> {saving ? "Saving..." : "Save"}
        </button>
      </div>

      <div className="grid lg:grid-cols-[220px_1fr_260px] gap-4">
        {/* LEFT: Add fields */}
        <div className="bg-white brutal-border brutal-shadow p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-3">// Add Fields</div>
          <button onClick={()=>addField("text")} className="w-full flex items-center gap-2 mb-2 bg-white text-black font-bold uppercase text-xs tracking-widest px-3 py-2 brutal-border brutal-shadow-sm brutal-hover" data-testid="add-text-btn">
            <Type className="w-4 h-4"/> Text Field
          </button>
          <button onClick={()=>addField("photo")} className="w-full flex items-center gap-2 bg-white text-black font-bold uppercase text-xs tracking-widest px-3 py-2 brutal-border brutal-shadow-sm brutal-hover" data-testid="add-photo-btn">
            <ImgIcon className="w-4 h-4"/> Photo Field
          </button>
          <div className="mt-6 font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">// Fields ({tpl.fields.length})</div>
          <div className="space-y-1 max-h-[300px] overflow-auto">
            {tpl.fields.map(f => (
              <div key={f.id} onClick={()=>setSelected(f.id)} className={`p-2 cursor-pointer font-mono text-xs border-2 ${selected === f.id ? "border-[#002FA7] bg-blue-50" : "border-black"}`}>
                <div className="flex items-center justify-between">
                  <span className="truncate">{f.label}</span>
                  <span className="text-[10px] text-gray-500 uppercase">{f.type}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CENTER: Canvas */}
        <div className="bg-[#E5E7EB] brutal-border brutal-shadow p-4 overflow-auto" style={{ minHeight: 400 }}>
          {!tpl.image_path ? (
            <div className="h-[400px] flex items-center justify-center border-2 border-dashed border-black bg-white">
              <div className="text-center">
                <Upload className="w-12 h-12 mx-auto mb-3 text-[#002FA7]"/>
                <div className="font-mono text-xs uppercase tracking-widest font-bold">Upload template image to start</div>
                <button onClick={()=>fileRef.current?.click()} className="mt-4 bg-[#002FA7] text-white font-bold uppercase text-xs tracking-widest px-4 py-2 brutal-border brutal-shadow-sm" data-testid="upload-cta">Choose file</button>
              </div>
            </div>
          ) : (
            <div className="pattern-checkerboard p-6 flex items-center justify-center min-h-[400px]">
              <div
                ref={canvasRef}
                className="relative brutal-border bg-white"
                style={{ width: tpl.image_width * canvasScale, height: tpl.image_height * canvasScale }}
                onClick={(e)=> { if(e.target === e.currentTarget) setSelected(null); }}
              >
                <img
                  src={fileUrl(tpl.image_path)}
                  alt=""
                  className="absolute inset-0 w-full h-full object-fill pointer-events-none"
                  draggable={false}
                />
                {tpl.fields.map(f => (
                  <Rnd
                    key={f.id}
                    size={{ width: f.width * canvasScale, height: f.height * canvasScale }}
                    position={{ x: f.x * canvasScale, y: f.y * canvasScale }}
                    onDragStop={(e, d) => updateField(f.id, { x: d.x / canvasScale, y: d.y / canvasScale })}
                    onResizeStop={(e, dir, ref, delta, pos) => updateField(f.id, {
                      width: parseFloat(ref.style.width) / canvasScale,
                      height: parseFloat(ref.style.height) / canvasScale,
                      x: pos.x / canvasScale,
                      y: pos.y / canvasScale,
                    })}
                    bounds="parent"
                    onClick={() => setSelected(f.id)}
                    className={`editor-field type-${f.type} ${selected === f.id ? "selected" : ""}`}
                    style={{
                      color: f.color,
                      fontSize: Math.max(f.font_size * canvasScale, 8),
                      fontWeight: f.font_weight === "bold" ? 700 : 400,
                      justifyContent: f.align === "center" ? "center" : f.align === "right" ? "flex-end" : "flex-start",
                    }}
                    data-testid={`canvas-field-${f.id}`}
                  >
                    {f.type === "photo" ? "PHOTO" : (f.sample_value || f.label)}
                  </Rnd>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: Properties */}
        <div className="bg-white brutal-border brutal-shadow p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-3">// Properties</div>
          {!sel ? (
            <div className="text-sm text-gray-500 font-mono">Select a field to edit</div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest font-bold mb-1">Label</label>
                <input value={sel.label} onChange={e=>updateField(sel.id, { label: e.target.value })} className="brutal-input w-full" data-testid="prop-label"/>
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest font-bold mb-1">Field Key (Excel column)</label>
                <input value={sel.key} onChange={e=>updateField(sel.id, { key: e.target.value.replace(/\s+/g, "_") })} className="brutal-input w-full" data-testid="prop-key"/>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest font-bold mb-1">X</label>
                  <input type="number" value={Math.round(sel.x)} onChange={e=>updateField(sel.id, { x: parseFloat(e.target.value)||0 })} className="brutal-input w-full"/>
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest font-bold mb-1">Y</label>
                  <input type="number" value={Math.round(sel.y)} onChange={e=>updateField(sel.id, { y: parseFloat(e.target.value)||0 })} className="brutal-input w-full"/>
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest font-bold mb-1">W</label>
                  <input type="number" value={Math.round(sel.width)} onChange={e=>updateField(sel.id, { width: parseFloat(e.target.value)||10 })} className="brutal-input w-full"/>
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest font-bold mb-1">H</label>
                  <input type="number" value={Math.round(sel.height)} onChange={e=>updateField(sel.id, { height: parseFloat(e.target.value)||10 })} className="brutal-input w-full"/>
                </div>
              </div>
              {sel.type === "text" && (
                <>
                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-widest font-bold mb-1">Font Size</label>
                    <input type="number" value={sel.font_size} onChange={e=>updateField(sel.id, { font_size: parseInt(e.target.value)||12 })} className="brutal-input w-full"/>
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-widest font-bold mb-1">Color</label>
                    <input type="color" value={sel.color} onChange={e=>updateField(sel.id, { color: e.target.value })} className="w-full h-10 border-2 border-black cursor-pointer"/>
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-widest font-bold mb-1">Weight</label>
                    <select value={sel.font_weight} onChange={e=>updateField(sel.id, { font_weight: e.target.value })} className="brutal-input w-full">
                      <option value="normal">Normal</option>
                      <option value="bold">Bold</option>
                    </select>
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-widest font-bold mb-1">Align</label>
                    <select value={sel.align} onChange={e=>updateField(sel.id, { align: e.target.value })} className="brutal-input w-full">
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                    </select>
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-widest font-bold mb-1">Preview Text</label>
                    <input value={sel.sample_value} onChange={e=>updateField(sel.id, { sample_value: e.target.value })} className="brutal-input w-full"/>
                  </div>
                </>
              )}
              <div className="flex gap-2 pt-3 border-t-2 border-black">
                <button onClick={()=>duplicateField(sel.id)} className="flex-1 flex items-center justify-center gap-1 bg-white border-2 border-black px-3 py-2 font-mono text-xs uppercase font-bold hover:bg-gray-100">
                  <Copy className="w-3 h-3"/> Dup
                </button>
                <button onClick={()=>removeField(sel.id)} className="flex-1 flex items-center justify-center gap-1 bg-[#FF3B30] text-white border-2 border-black px-3 py-2 font-mono text-xs uppercase font-bold">
                  <Trash2 className="w-3 h-3"/> Del
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
